import React from 'react';
import { Minus, Plus, ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react';
import { computeDiff, groupIntoHunks, type DiffHunk, type DiffLine } from '../utils/diffEngine';
import type { Annotation } from '../services/tenancyReviewRewrite';

interface DiffViewProps {
  originalText: string;
  rewrittenText: string;
  annotations: Annotation[];
  activeHighlight: string | null;
  onHunkClick?: (annotationId: string) => void;
  hunkRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'border-l-red-500',
  high: 'border-l-gray-700',
  medium: 'border-l-gray-500',
  low: 'border-l-gray-300',
};

function findAnnotationForHunk(
  hunk: DiffHunk,
  annotations: Annotation[],
): Annotation | null {
  const removedText = hunk.lines
    .filter(l => l.type === 'removed')
    .map(l => l.text)
    .join(' ')
    .toLowerCase();

  const addedText = hunk.lines
    .filter(l => l.type === 'added')
    .map(l => l.text)
    .join(' ')
    .toLowerCase();

  if (!removedText && !addedText) return null;

  let bestMatch: Annotation | null = null;
  let bestScore = 0;

  for (const ann of annotations) {
    let score = 0;
    const origLower = (ann.originalText || '').toLowerCase();
    const newLower = (ann.newText || '').toLowerCase();

    if (origLower && removedText.includes(origLower.slice(0, 40))) {
      score += 3;
    } else if (origLower) {
      const origWords = origLower.split(/\s+/).filter(w => w.length > 3);
      const matchingWords = origWords.filter(w => removedText.includes(w));
      score += matchingWords.length / Math.max(origWords.length, 1);
    }

    if (newLower && addedText.includes(newLower.slice(0, 40))) {
      score += 3;
    } else if (newLower) {
      const newWords = newLower.split(/\s+/).filter(w => w.length > 3);
      const matchingWords = newWords.filter(w => addedText.includes(w));
      score += matchingWords.length / Math.max(newWords.length, 1);
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = ann;
    }
  }

  return bestScore >= 0.5 ? bestMatch : null;
}

function DiffLineRow({ line }: { line: DiffLine }) {
  const bgClass =
    line.type === 'removed'
      ? 'bg-red-50'
      : line.type === 'added'
        ? 'bg-gray-100'
        : 'bg-white';

  const textClass =
    line.type === 'removed'
      ? 'text-red-800'
      : line.type === 'added'
        ? 'text-gray-800'
        : 'text-gray-600';

  const gutterBg =
    line.type === 'removed'
      ? 'bg-red-100/80 text-red-400'
      : line.type === 'added'
        ? 'bg-gray-200/80 text-gray-500'
        : 'bg-gray-50 text-gray-300';

  const iconClass =
    line.type === 'removed'
      ? 'text-red-400'
      : line.type === 'added'
        ? 'text-gray-500'
        : '';

  return (
    <div className={`flex items-stretch text-xs font-mono leading-5 ${bgClass} group min-w-0`}>
      <div className={`w-10 flex-shrink-0 text-right pr-2 select-none ${gutterBg}`}>
        {line.oldLineNumber ?? ''}
      </div>
      <div className={`w-10 flex-shrink-0 text-right pr-2 select-none ${gutterBg}`}>
        {line.newLineNumber ?? ''}
      </div>
      <div className="w-5 flex-shrink-0 flex items-center justify-center select-none">
        {line.type === 'removed' && <Minus className={`w-3 h-3 ${iconClass}`} />}
        {line.type === 'added' && <Plus className={`w-3 h-3 ${iconClass}`} />}
      </div>
      <div className={`flex-1 min-w-0 px-2 whitespace-pre-wrap break-words overflow-wrap-anywhere ${textClass}`} style={{ overflowWrap: 'anywhere' }}>
        {line.text || '\u00A0'}
      </div>
    </div>
  );
}

function HunkHeader({
  hunk,
  annotation,
  collapsed,
  onToggle,
}: {
  hunk: DiffHunk;
  annotation: Annotation | null;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const added = hunk.lines.filter(l => l.type === 'added').length;
  const removed = hunk.lines.filter(l => l.type === 'removed').length;

  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-1.5 bg-gray-100 border-y border-gray-200 text-xs text-gray-500 hover:bg-gray-150 transition-colors"
    >
      {collapsed ? (
        <ChevronRight className="w-3.5 h-3.5" />
      ) : (
        <ChevronDown className="w-3.5 h-3.5" />
      )}
      <span className="font-mono">
        @@ -{hunk.oldStart} +{hunk.newStart} @@
      </span>
      {removed > 0 && <span className="text-red-500 font-medium">-{removed}</span>}
      {added > 0 && <span className="text-gray-700 font-medium">+{added}</span>}
      {annotation && (
        <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-medium">
          {annotation.category}
        </span>
      )}
    </button>
  );
}

function CollapsedIndicator({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-center gap-2 py-1 bg-gray-50 border-y border-gray-100 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
    >
      <MoreHorizontal className="w-3.5 h-3.5" />
      {count} unchanged lines
    </button>
  );
}

export function DiffView({
  originalText,
  rewrittenText,
  annotations,
  activeHighlight,
  onHunkClick,
  hunkRefs,
}: DiffViewProps) {
  const diffLines = React.useMemo(
    () => computeDiff(originalText, rewrittenText),
    [originalText, rewrittenText],
  );

  const hunks = React.useMemo(
    () => groupIntoHunks(diffLines, 3),
    [diffLines],
  );

  const hunkAnnotations = React.useMemo(() => {
    const map = new Map<string, Annotation | null>();
    const usedAnnotations = new Set<string>();
    for (const hunk of hunks) {
      if (!hunk.hasChanges) {
        map.set(hunk.id, null);
        continue;
      }
      const match = findAnnotationForHunk(hunk, annotations.filter(a => !usedAnnotations.has(a.id)));
      if (match) usedAnnotations.add(match.id);
      map.set(hunk.id, match);
    }
    return map;
  }, [hunks, annotations]);

  const [collapsedHunks, setCollapsedHunks] = React.useState<Set<string>>(new Set());

  const toggleHunk = (id: string) => {
    setCollapsedHunks(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const totalAdded = diffLines.filter(l => l.type === 'added').length;
  const totalRemoved = diffLines.filter(l => l.type === 'removed').length;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-red-600 font-semibold">-{totalRemoved}</span>
          <span className="text-gray-400">removed</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-700 font-semibold">+{totalAdded}</span>
          <span className="text-gray-400">added</span>
        </div>
        <div className="flex-1" />
        <div className="flex gap-1">
          <div className="w-3 h-3 rounded-sm bg-red-200 border border-red-300" />
          <span className="text-xs text-gray-400">Original</span>
          <div className="w-3 h-3 rounded-sm bg-gray-300 border border-gray-400 ml-2" />
          <span className="text-xs text-gray-400">Rewritten</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {hunks.map((hunk, hunkIdx) => {
          const annotation = hunkAnnotations.get(hunk.id) ?? null;
          const isCollapsed = collapsedHunks.has(hunk.id);
          const isActive = annotation && activeHighlight === annotation.id;
          const borderClass = annotation ? SEVERITY_COLORS[annotation.severity] || '' : '';

          if (!hunk.hasChanges) {
            return (
              <CollapsedIndicator
                key={hunk.id}
                count={hunk.lines.length}
                onClick={() => toggleHunk(hunk.id)}
              />
            );
          }

          return (
            <div
              key={hunk.id}
              ref={el => {
                if (hunkRefs && annotation) {
                  hunkRefs.current[annotation.id] = el;
                }
              }}
              className={`
                ${borderClass ? `border-l-3 ${borderClass}` : ''}
                ${isActive ? 'ring-2 ring-inset ring-gray-300 bg-gray-50/50' : ''}
                transition-all duration-300
              `}
              onClick={() => {
                if (annotation && onHunkClick) {
                  onHunkClick(annotation.id);
                }
              }}
              style={{ cursor: annotation ? 'pointer' : 'default' }}
            >
              <HunkHeader
                hunk={hunk}
                annotation={annotation}
                collapsed={isCollapsed}
                onToggle={() => toggleHunk(hunk.id)}
              />
              {!isCollapsed && (
                <div>
                  {hunk.lines.map((line, lineIdx) => (
                    <DiffLineRow key={`${hunkIdx}-${lineIdx}`} line={line} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
